"use client";

import { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import type { LongShortPricePoint, Point } from "@/lib/types";
import { mergeSeriesByDate } from "@/lib/macro";

export const RORO_REGIME_COLORS: Record<string, string> = {
  "Strong bull": "#22c55e",
  "Basic risk-on": "#38bdf8",
  "Neutral risk-on": "#a78bfa",
  "Neutral risk-off": "#f59e0b",
  "Basic risk-off": "#fb7185",
  "Strong bear": "#ef4444"
};

const axisStyle = { fill: "#94a3b8", fontSize: 11 };
const tooltipStyle = {
  background: "rgba(2,6,23,0.97)",
  border: "1px solid rgba(148,163,184,0.35)",
  borderRadius: 8,
  color: "#f8fafc",
  fontSize: 12,
  boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
  padding: "8px 12px"
};
const tooltipLabelStyle = { color: "#ffffff", fontWeight: 700, marginBottom: 4 };
const tooltipItemStyle = { color: "#ffffff", padding: 0 };
// Subtle hover highlight for bar charts (recharts' default is an opaque light-grey block).
const barCursor = { fill: "rgba(148,163,184,0.08)" };
const PALETTE = ["#6b9dff", "#c5a5ff", "#43d6a0", "#f7c05a", "#ff737e", "#90a4bd"];
const LINE_DASHES = [undefined, "7 4", "2 4", "10 3 2 3"];

/** Clickable legend chips that toggle which series are visible. */
function ToggleLegend({
  items,
  hidden,
  onToggle
}: {
  items: Array<{ key: string; label: string; color: string }>;
  hidden: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {items.map((item) => {
        const off = hidden.has(item.key);
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onToggle(item.key)}
            aria-pressed={!off}
            className={`flex min-h-11 items-center gap-1.5 rounded-md border px-3 py-2 text-xs transition-colors ${
              off ? "border-white/10 text-[var(--hc-muted)]" : "border-white/20 text-slate-200"
            }`}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: off ? "#475569" : item.color }} />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export function ToggleableLineChart({
  series,
  height = 288
}: {
  series: Array<{ key: string; label: string; points: Point[] }>;
  height?: number;
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const items = series.map((item, index) => ({ key: item.key, label: item.label, color: PALETTE[index % PALETTE.length] }));
  const rows = useMemo(() => mergeSeriesByDate(series), [series]);
  const yDomain = useMemo(() => {
    const visibleKeys = items.filter((item) => !hidden.has(item.key)).map((item) => item.key);
    const values = rows.flatMap((row) => visibleKeys.map((key) => Number(row[key])).filter(Number.isFinite));
    if (!values.length) return ["auto", "auto"] as [string, string];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const spread = Math.max(max - min, 1);
    const padding = Math.max(spread * 0.22, 0.75);
    return [Number((min - padding).toFixed(2)), Number((max + padding).toFixed(2))] as [number, number];
  }, [hidden, items, rows]);

  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <div>
      <ToggleLegend items={items} hidden={hidden} onToggle={toggle} />
      <div role="img" aria-label={`Time-series comparison with ${series.map((item) => item.label).join(", ")}. Missing dates are gaps.`} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
            <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
            <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false} />
            <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={52} domain={yDomain} tickCount={5} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ stroke: "#64748b", strokeWidth: 1 }} />
            {items.map((item) =>
              hidden.has(item.key) ? null : (
                <Line
                  key={item.key}
                  type="linear"
                  dataKey={item.key}
                  name={item.label}
                  stroke={item.color}
                  strokeDasharray={LINE_DASHES[items.findIndex((candidate) => candidate.key === item.key) % LINE_DASHES.length]}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4, stroke: "#f8fafc", strokeWidth: 1 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function fourHourLabel(value: string) {
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.valueOf())) return value;
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    hour12: false,
    timeZone: "UTC"
  }).format(timestamp);
}

export function LongShortPriceChart({
  points,
  livePrice,
  livePriceAt,
  height = 520
}: {
  points: LongShortPricePoint[];
  livePrice?: number | null;
  livePriceAt?: string | null;
  height?: number;
}) {
  const chartPoints = useMemo(() => {
    const rows: Array<{
      time: number;
      date: string;
      net_long_contracts: number | null;
      net_short_contracts: number | null;
      price: number;
      observation: "snapshot" | "live-price";
    }> = points.map((point) => ({
      time: point.time,
      date: point.date,
      net_long_contracts: point.net_long_contracts,
      net_short_contracts: point.net_short_contracts,
      price: point.price,
      observation: "snapshot"
    }));

    if (!Number.isFinite(livePrice) || !livePriceAt || rows.length === 0) return rows;
    const liveTime = Date.parse(livePriceAt);
    const latestSnapshotTime = rows.at(-1)?.time ?? 0;
    if (!Number.isFinite(liveTime) || liveTime <= latestSnapshotTime) return rows;

    rows.push({
      time: liveTime,
      date: new Date(liveTime).toISOString(),
      net_long_contracts: null,
      net_short_contracts: null,
      price: livePrice as number,
      observation: "live-price"
    });
    return rows;
  }, [livePrice, livePriceAt, points]);

  return (
    <div>
      {chartPoints.at(-1)?.observation === "live-price" ? (
        <p className="mb-2 text-xs leading-5 text-[var(--hc-muted)]">
          Green and red positioning lines stop at the last CoinGlass snapshot. The white BTC line extends to the latest Binance price.
        </p>
      ) : null}
      <div
        role="img"
        aria-label="Four-hour BTC chart with CoinGlass Net Long and Net Short contracts and a BTC perpetual price line extended to the latest Binance observation"
        style={{ height }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartPoints} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke="#253044" strokeOpacity={0.8} vertical={false} />
            <XAxis
              dataKey="date"
              tick={axisStyle}
              tickFormatter={fourHourLabel}
              axisLine={false}
              tickLine={false}
              minTickGap={54}
            />
            <YAxis
              yAxisId="ratio"
              domain={["auto", "auto"]}
              tick={axisStyle}
              tickFormatter={(value: number) => new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value)}
              axisLine={false}
              tickLine={false}
              width={58}
            />
            <YAxis
              yAxisId="price"
              orientation="right"
              domain={["auto", "auto"]}
              tick={axisStyle}
              tickFormatter={(value: number) => `$${Math.round(value / 1000)}k`}
              axisLine={false}
              tickLine={false}
              width={58}
            />
            <Legend
              align="left"
              verticalAlign="top"
              height={42}
              iconType="plainline"
              iconSize={22}
              wrapperStyle={{ color: "#9ba6b4", fontSize: 12 }}
            />
            <ReferenceLine yAxisId="ratio" y={0} stroke="#607089" strokeDasharray="5 5" strokeOpacity={0.7} />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              labelFormatter={fourHourLabel}
              cursor={{ stroke: "#64748b", strokeWidth: 1 }}
              formatter={(value: number, name: string) =>
                name === "BTC price"
                  ? [`$${value.toLocaleString("en", { maximumFractionDigits: 0 })}`, name]
                  : [`${value.toLocaleString("en", { maximumFractionDigits: 2 })} contracts`, name]
              }
            />
            <Line yAxisId="ratio" type="linear" dataKey="net_long_contracts" name="Net Long" stroke="#39d3a3" strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            <Line yAxisId="ratio" type="linear" dataKey="net_short_contracts" name="Net Short" stroke="#ff6b84" strokeWidth={2.4} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
            <Line yAxisId="price" type="linear" dataKey="price" name="BTC price" stroke="#eef2f7" strokeWidth={2.1} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <ul className="sr-only">
        {chartPoints.map((point) => (
          <li key={`${point.time}-${point.observation}`}>
            {point.date}: {point.observation === "live-price" ? `Live Binance BTC $${point.price}` : `Net Long ${point.net_long_contracts} contracts, Net Short ${point.net_short_contracts} contracts, BTC $${point.price}`}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Risk-on/Risk-off regime: −100..+100 score columns colored by regime, with the BTC price line. */
export function RoroRegimeChart({
  series,
  height = 320
}: {
  series: Array<{ date: string; score: number; regime: string; btc: number }>;
  height?: number;
}) {
  const regimesPresent = Array.from(new Set(series.map((d) => d.regime)));
  const order = Object.keys(RORO_REGIME_COLORS);
  const legend = order.filter((r) => regimesPresent.includes(r));
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-400">
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 bg-slate-100" /> BTC Price
        </span>
        {legend.map((regime) => (
          <span key={regime} className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-sm" style={{ background: RORO_REGIME_COLORS[regime] }} />
            {regime}
          </span>
        ))}
      </div>
      <p className="sr-only">Risk-on/risk-off score from minus 100 to 100 with BTC price. Bar direction and text labels supplement color. Zero-score days are shown as a small neutral tick on the baseline.</p>
      <div role="img" aria-label="Risk-on and risk-off regime score with BTC price" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={28} />
            <YAxis
              yAxisId="score"
              domain={[-100, 100]}
              ticks={[-100, -50, 0, 50, 100]}
              tick={axisStyle}
              tickFormatter={(value: number) => value > 0 ? `+${value}` : String(value)}
              axisLine={false}
              tickLine={false}
              width={46}
            />
            <YAxis
              yAxisId="btc"
              orientation="right"
              domain={["auto", "auto"]}
              tick={axisStyle}
              axisLine={false}
              tickLine={false}
              width={52}
              tickFormatter={(value: number) => `${Math.round(value / 1000)}k`}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={barCursor}
              formatter={(value: number, name: string) =>
                name === "btc" ? [`$${value.toLocaleString()}`, "BTC"] : [value, "Score"]
              }
            />
            <ReferenceLine yAxisId="score" y={0} stroke="#cbd5e1" strokeOpacity={0.85} />
            <Bar
              yAxisId="score"
              dataKey="score"
              name="score"
              radius={[3, 3, 0, 0]}
              minPointSize={3}
              isAnimationActive={false}
            >
              {series.map((d, index) => (
                <Cell key={index} fill={RORO_REGIME_COLORS[d.regime] ?? "#64748b"} />
              ))}
            </Bar>
            <Line yAxisId="btc" type="linear" dataKey="btc" name="btc" stroke="#f8fafc" strokeWidth={2} dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Daily BTC ETF net flow bars centered on zero, using the same cross-asset visual language as RORO. */
export function EtfFlowChart({ points, height = 320 }: { points: Point[]; height?: number }) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-400">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-emerald-500" /> Net inflow</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-rose-500" /> Net outflow</span>
      </div>
      <div role="img" aria-label="Daily spot Bitcoin ETF net flows in millions of US dollars" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
            <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false} minTickGap={28} />
            <YAxis
              tick={axisStyle}
              axisLine={false}
              tickLine={false}
              width={60}
              tickFormatter={(value: number) => `${value >= 0 ? "+" : ""}${Math.round(value)}m`}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={barCursor}
              formatter={(value: number) => [`${value >= 0 ? "+" : ""}$${value.toFixed(1)}m`, "Net flow"]}
            />
            <ReferenceLine y={0} stroke="#cbd5e1" strokeOpacity={0.85} />
            <Bar dataKey="value" name="Net flow" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {points.map((point) => <Cell key={point.date} fill={point.value >= 0 ? "#22c55e" : "#f43f5e"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ul className="sr-only">
        {points.map((point) => <li key={point.date}>{point.date}: {point.value >= 0 ? "inflow" : "outflow"} ${Math.abs(point.value)} million</li>)}
      </ul>
    </div>
  );
}

export function AreaSeriesChart({ points, color = "#6b9dff", height = 192 }: { points: Point[]; color?: string; height?: number }) {
  return (
    <div role="img" aria-label={`Time series with ${points.length} observations`} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={56} domain={["auto", "auto"]} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={{ stroke: "#64748b", strokeWidth: 1 }} />
          <Area type="linear" dataKey="value" stroke={color} fill={color} fillOpacity={0.12} strokeWidth={2} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Performance bars that read the change for the selected period. */
export function PerformanceBars({
  data,
  period
}: {
  data: Array<{ symbol: string; name: string; change: number; changes?: Record<string, number | null>; group: string }>;
  period: string;
}) {
  const isFlow = period === "flow";
  const rows = data.map((item) => ({
    symbol: item.symbol,
    value: item.changes?.[period] ?? item.change
  }));
  return (
    <div role="img" aria-label={`${isFlow ? "ETF flows" : `${period} performance`}. Positive values extend above zero; negative values extend below zero.`} className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 10, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="symbol" tick={axisStyle} axisLine={false} tickLine={false} minTickGap={12} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={54} unit={isFlow ? "" : "%"} tickFormatter={(value: number) => (isFlow ? `${value}` : `${value}`)} />
          <Tooltip
            contentStyle={tooltipStyle}
            labelStyle={tooltipLabelStyle}
            itemStyle={tooltipItemStyle}
            cursor={barCursor}
            formatter={(value: number) => [isFlow ? `$${value?.toFixed(1)}m` : `${value?.toFixed(2)}%`, isFlow ? "ETF flow" : period]}
          />
          <ReferenceLine y={0} stroke="#475569" />
          <Bar dataKey="value" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {rows.map((item) => (
              <Cell key={item.symbol} fill={item.value >= 0 ? "#22c55e" : "#f43f5e"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ul className="sr-only">
        {rows.map((item) => <li key={item.symbol}>{item.symbol}: {item.value >= 0 ? "positive" : "negative"} {item.value}</li>)}
      </ul>
    </div>
  );
}

/** FED forward rate curve: line + dots with a semi-transparent policy-rate reference line. */
export function ForwardCurveChart({ data }: { data: Array<{ tenor: string; value: number; policy_rate: number }> }) {
  const policy = data[0]?.policy_rate;
  return (
    <div role="img" aria-label="Implied forward rate curve compared with current policy rate" className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="tenor" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={48} unit="%" domain={["auto", "auto"]} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => `${value?.toFixed(2)}%`} />
          {policy !== undefined && (
            <ReferenceLine
              y={policy}
              stroke="#f59e0b"
              strokeOpacity={0.5}
              strokeDasharray="4 4"
              label={{ value: `FED ${policy}%`, fill: "#f59e0b", fontSize: 11, position: "insideTopRight" }}
            />
          )}
          <Line type="linear" dataKey="value" name="Implied forward" stroke="#6b9dff" strokeWidth={2} dot={{ r: 4, fill: "#6b9dff" }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Histogram with a hard zero line; bars colored by sign (OIS minus policy). */
export function HistogramChart({ data }: { data: Array<{ tenor: string; value: number }> }) {
  return (
    <div role="img" aria-label="OIS spread to current policy rate. Positive bars extend above zero and negative bars below zero." className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 10, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="tenor" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={44} tickFormatter={(value: number) => value.toFixed(1)} />
          <Tooltip
            contentStyle={tooltipStyle}
            labelStyle={tooltipLabelStyle}
            itemStyle={tooltipItemStyle}
            cursor={barCursor}
            formatter={(value: number) => [`${value?.toFixed(2)}%`, "spread"]}
          />
          <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} />
          <Bar dataKey="value" radius={[3, 3, 0, 0]} isAnimationActive={false}>
            {data.map((item) => (
              <Cell key={item.tenor} fill={item.value >= 0 ? "#22c55e" : "#f43f5e"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** FOMC statement index: hawkish-dovish + optimism grouped bars on one scale. */
export function FomcBars({ data }: { data: Array<{ date: string; hawkish_dovish: number; optimism: number }> }) {
  return (
    <div role="img" aria-label="FOMC hawkish-dovish and optimism indices" className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 10, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} cursor={barCursor} />
          <Legend wrapperStyle={{ color: "#e2e8f0", fontSize: 11 }} />
          <ReferenceLine y={0} stroke="#475569" />
          <Bar dataKey="hawkish_dovish" name="Hawkish-Dovish (solid)" fill="#ff737e" radius={[3, 3, 0, 0]} isAnimationActive={false} />
          <Bar dataKey="optimism" name="Optimism (outlined)" fill="#6b9dff" stroke="#f3f5f7" strokeWidth={1} radius={[3, 3, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Vol term structure: a single curve across maturities. */
export function VolTermChart({ data }: { data: Array<{ tenor: string; value: number }> }) {
  return (
    <div role="img" aria-label="ATM volatility term structure by tenor" className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="tenor" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} unit="%" domain={["auto", "auto"]} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => `${value?.toFixed(1)}%`} />
          <Line type="linear" dataKey="value" name="ATM IV" stroke="#c5a5ff" strokeWidth={2} dot={{ r: 4, fill: "#c5a5ff" }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/** 25Δ skew by tenor (put IV − call IV); negative = put skew. */
export function SkewChart({ data }: { data: Array<{ tenor: string; value: number }> }) {
  return (
    <div role="img" aria-label="25 delta skew by tenor with zero reference" className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: -10 }}>
          <CartesianGrid stroke="#243044" strokeOpacity={0.75} vertical={false} />
          <XAxis dataKey="tenor" tick={axisStyle} axisLine={false} tickLine={false} />
          <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={40} tickFormatter={(value: number) => value.toFixed(1)} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => `${value?.toFixed(2)} vol`} />
          <ReferenceLine y={0} stroke="#94a3b8" strokeWidth={1.5} />
          <Line type="linear" dataKey="value" name="25Δ skew" stroke="#ff737e" strokeWidth={2} dot={{ r: 4, fill: "#ff737e" }} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
